import React, { useState, useEffect, useRef } from "react";
import { Button, Form } from "react-bootstrap";
import * as Icon from "lucide-react";
import ApiService from "../../../_services/ApiService";

/**
 * Authoring UI for a Spin Wheel Game (study-resource TypeId 41). Edits the
 * wedge JSON that is stored in study_resource_master.Content and read at play
 * time by the ilearn <SpinWheelGame> renderer + the nodefork wheel controller.
 *
 * Config shape written back:
 *   { title, spinsToComplete, questionTimerSec,
 *     wedges: [ { id, label, type:"topic"|"special", topicTagId?, topicLabel?,
 *                 special?, coins?, color, weight } ] }
 */

interface Wedge {
  id: number;
  label: string;
  type: "topic" | "special";
  topicTagId?: number | null;
  topicLabel?: string;
  special?: "double" | "jackpot" | "mystery" | "loseturn";
  coins?: number;
  color: string;
  weight: number;
}

interface WheelConfig {
  title: string;
  spinsToComplete: number;
  questionTimerSec: number;
  wedges: Wedge[];
}

// Wedge colours reference the shared chart-token palette (theme-aware in both
// iteach + ilearn) — never hardcoded hex, per the theming rules.
const PALETTE = [
  "var(--chart-1)", "var(--chart-2)", "var(--chart-3)", "var(--chart-4)",
  "var(--chart-5)", "var(--chart-6)", "var(--chart-7)", "var(--chart-8)",
];

const SPECIALS: { value: Wedge["special"]; label: string }[] = [
  { value: "double", label: "Double (next win 2×)" },
  { value: "jackpot", label: "Jackpot (big coins)" },
  { value: "mystery", label: "Mystery (random coins)" },
  { value: "loseturn", label: "Lose a Turn" },
];

const DEFAULT_CONFIG: WheelConfig = {
  title: "",
  spinsToComplete: 3,
  questionTimerSec: 30,
  wedges: [],
};

const parse = (content: string | undefined): WheelConfig => {
  try {
    const c = JSON.parse(content || "{}");
    return {
      title: c.title || "",
      spinsToComplete: Number(c.spinsToComplete) || 3,
      questionTimerSec: Number(c.questionTimerSec) || 30,
      wedges: Array.isArray(c.wedges) ? c.wedges : [],
    };
  } catch {
    return { ...DEFAULT_CONFIG };
  }
};

interface TopicOption { id: number; name: string; displayName?: string; courseName?: string; }

/** Per-wedge topic search (TagTypeId 9 Topics via v1/topics/search). */
const TopicPicker: React.FC<{
  value?: number | null;
  label?: string;
  onPick: (id: number, label: string) => void;
}> = ({ value, label, onPick }) => {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<TopicOption[]>([]);
  const [open, setOpen] = useState(false);
  const [searching, setSearching] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    if (!query || query.length < 2) { setResults([]); return; }
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await ApiService.searchTopics(query);
        const data = (res.data as any)?.data || (res.data as any) || [];
        setResults(Array.isArray(data) ? data.slice(0, 12) : []);
      } catch {
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 250);
    return () => timer.current && clearTimeout(timer.current);
  }, [query]);

  return (
    <div style={{ position: "relative" }}>
      {value ? (
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{
            background: "var(--brand-primary-subtle, #eef)", color: "var(--brand-primary)",
            padding: "4px 10px", borderRadius: 6, fontSize: 13, fontWeight: 600,
          }}>
            {label || `Topic #${value}`}
          </span>
          <Button variant="link" size="sm" style={{ padding: 0 }} onClick={() => { onPick(0, ""); setQuery(""); }}>
            change
          </Button>
        </div>
      ) : (
        <>
          <Form.Control
            type="text"
            placeholder="Search a topic…"
            value={query}
            onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
            onFocus={() => setOpen(true)}
          />
          {open && (query.length >= 2) && (
            <div style={{
              position: "absolute", zIndex: 20, top: "100%", left: 0, right: 0,
              background: "var(--surface-card, #fff)", border: "1px solid var(--border-default, #ddd)",
              borderRadius: 6, maxHeight: 220, overflowY: "auto", boxShadow: "0 4px 14px rgba(0,0,0,0.12)",
            }}>
              {searching && <div style={{ padding: 10, color: "var(--text-muted)" }}>Searching…</div>}
              {!searching && results.length === 0 && <div style={{ padding: 10, color: "var(--text-muted)" }}>No topics found</div>}
              {results.map((t) => (
                <div
                  key={t.id}
                  onClick={() => { onPick(t.id, t.displayName || t.name); setOpen(false); setQuery(""); }}
                  style={{ padding: "8px 10px", cursor: "pointer", fontSize: 13, borderBottom: "1px solid var(--border-subtle, #f0f0f0)" }}
                  onMouseDown={(e) => e.preventDefault()}
                >
                  <div style={{ fontWeight: 600 }}>{t.displayName || t.name}</div>
                  {t.courseName && <div style={{ color: "var(--text-muted)", fontSize: 11 }}>{t.courseName}</div>}
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
};

const SpinWheelEditor: React.FC<{ content?: string; onChange: (json: string) => void }> = ({ content, onChange }) => {
  const config = parse(content);

  const commit = (next: Partial<WheelConfig>) => {
    onChange(JSON.stringify({ ...config, ...next }, null, 2));
  };

  const updateWedge = (i: number, patch: Partial<Wedge>) => {
    const wedges = config.wedges.map((w, idx) => (idx === i ? { ...w, ...patch } : w));
    commit({ wedges });
  };

  const addWedge = (type: Wedge["type"]) => {
    const nextId = (config.wedges.reduce((m, w) => Math.max(m, Number(w.id) || 0), 0) || 0) + 1;
    const color = PALETTE[config.wedges.length % PALETTE.length];
    const wedge: Wedge =
      type === "topic"
        ? { id: nextId, label: "New topic", type: "topic", topicTagId: null, coins: 50, color, weight: 2 }
        : { id: nextId, label: "Jackpot", type: "special", special: "jackpot", coins: 200, color, weight: 1 };
    commit({ wedges: [...config.wedges, wedge] });
  };

  const removeWedge = (i: number) => commit({ wedges: config.wedges.filter((_, idx) => idx !== i) });

  return (
    <div>
      {/* Global wheel settings */}
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 16 }}>
        <Form.Group style={{ flex: "2 1 220px", marginBottom: 0 }}>
          <Form.Label>Wheel title</Form.Label>
          <Form.Control
            type="text"
            placeholder="e.g. Algebra Spin Challenge"
            value={config.title}
            onChange={(e) => commit({ title: e.target.value })}
          />
        </Form.Group>
        <Form.Group style={{ flex: "1 1 120px", marginBottom: 0 }}>
          <Form.Label>Spins to complete</Form.Label>
          <Form.Control
            type="number"
            min={1}
            value={config.spinsToComplete}
            onChange={(e) => commit({ spinsToComplete: Math.max(1, Number(e.target.value) || 1) })}
          />
        </Form.Group>
        <Form.Group style={{ flex: "1 1 120px", marginBottom: 0 }}>
          <Form.Label>Answer timer (sec)</Form.Label>
          <Form.Control
            type="number"
            min={5}
            value={config.questionTimerSec}
            onChange={(e) => commit({ questionTimerSec: Math.max(5, Number(e.target.value) || 5) })}
          />
        </Form.Group>
      </div>

      {/* Wedges */}
      <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "var(--text-muted)", marginBottom: 8 }}>
        Wedges ({config.wedges.length})
      </div>

      {config.wedges.length === 0 && (
        <div style={{ padding: 16, textAlign: "center", color: "var(--text-muted)", border: "1px dashed var(--border-default, #ccc)", borderRadius: 8, marginBottom: 12 }}>
          No wedges yet. Add a few topic wedges and some specials for a lively wheel.
        </div>
      )}

      {config.wedges.map((w, i) => (
        <div key={w.id} style={{ border: "1px solid var(--border-default, #ddd)", borderLeft: `5px solid ${w.color}`, borderRadius: 8, padding: 12, marginBottom: 10, background: "var(--surface-card, #fff)" }}>
          <div style={{ display: "flex", gap: 10, alignItems: "flex-start", flexWrap: "wrap" }}>
            <Form.Group style={{ flex: "1 1 160px", marginBottom: 0 }}>
              <Form.Label style={{ fontSize: 12 }}>Label</Form.Label>
              <Form.Control size="sm" type="text" value={w.label} onChange={(e) => updateWedge(i, { label: e.target.value })} />
            </Form.Group>

            <Form.Group style={{ flex: "0 0 130px", marginBottom: 0 }}>
              <Form.Label style={{ fontSize: 12 }}>Type</Form.Label>
              <Form.Control
                as="select"
                size="sm"
                value={w.type}
                onChange={(e) => updateWedge(i, e.target.value === "topic"
                  ? { type: "topic", special: undefined, topicTagId: w.topicTagId ?? null }
                  : { type: "special", special: w.special || "jackpot", topicTagId: null })}
              >
                <option value="topic">Topic question</option>
                <option value="special">Special</option>
              </Form.Control>
            </Form.Group>

            <Form.Group style={{ flex: "0 0 90px", marginBottom: 0 }}>
              <Form.Label style={{ fontSize: 12 }}>Coins</Form.Label>
              <Form.Control
                size="sm"
                type="number"
                min={0}
                value={w.coins ?? 0}
                disabled={w.type === "special" && (w.special === "double" || w.special === "loseturn")}
                onChange={(e) => updateWedge(i, { coins: Math.max(0, Number(e.target.value) || 0) })}
              />
            </Form.Group>

            <Form.Group style={{ flex: "0 0 80px", marginBottom: 0 }}>
              <Form.Label style={{ fontSize: 12 }}>Weight</Form.Label>
              <Form.Control
                size="sm"
                type="number"
                min={1}
                value={w.weight ?? 1}
                onChange={(e) => updateWedge(i, { weight: Math.max(1, Number(e.target.value) || 1) })}
              />
            </Form.Group>

            <Button variant="link" style={{ color: "var(--status-danger, #c00)", padding: "26px 0 0" }} onClick={() => removeWedge(i)} title="Remove wedge">
              <Icon.Trash2 size={16} />
            </Button>
          </div>

          <div style={{ display: "flex", gap: 10, alignItems: "flex-end", flexWrap: "wrap", marginTop: 10 }}>
            {w.type === "topic" ? (
              <div style={{ flex: "1 1 260px" }}>
                <Form.Label style={{ fontSize: 12 }}>Topic</Form.Label>
                <TopicPicker
                  value={w.topicTagId}
                  label={w.topicLabel}
                  onPick={(id, label) => updateWedge(i, { topicTagId: id || null, topicLabel: label, label: label || w.label })}
                />
              </div>
            ) : (
              <Form.Group style={{ flex: "0 0 220px", marginBottom: 0 }}>
                <Form.Label style={{ fontSize: 12 }}>Special effect</Form.Label>
                <Form.Control as="select" size="sm" value={w.special} onChange={(e) => updateWedge(i, { special: e.target.value as Wedge["special"] })}>
                  {SPECIALS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                </Form.Control>
              </Form.Group>
            )}

            <Form.Group style={{ flex: "0 0 auto", marginBottom: 0 }}>
              <Form.Label style={{ fontSize: 12 }}>Colour</Form.Label>
              <div style={{ display: "flex", gap: 6 }}>
                {PALETTE.map((c) => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => updateWedge(i, { color: c })}
                    title={c}
                    style={{
                      width: 22, height: 22, borderRadius: "50%", background: c, cursor: "pointer",
                      border: w.color === c ? "3px solid var(--text-strong, #333)" : "1px solid var(--border-default, #ccc)",
                    }}
                  />
                ))}
              </div>
            </Form.Group>
          </div>
        </div>
      ))}

      <div style={{ display: "flex", gap: 8, marginTop: 6 }}>
        <Button variant="secondary" size="sm" onClick={() => addWedge("topic")}>
          <Icon.Plus size={14} /> Add topic wedge
        </Button>
        <Button variant="secondary" size="sm" onClick={() => addWedge("special")}>
          <Icon.Star size={14} /> Add special wedge
        </Button>
      </div>

      <Form.Text className="text-muted" style={{ display: "block", marginTop: 10 }}>
        Landing on a topic wedge serves a random question from that topic; a correct answer wins the wedge's coins.
        Weight biases how often a wedge is landed on. Issue this wheel to courses/batches like any study resource.
      </Form.Text>
    </div>
  );
};

export default SpinWheelEditor;
